import type { Notification } from "../types/notifications";
import type { NotificationApiItem } from "../services/types";

const NOTIFICATION_STORAGE_KEY = "airbus_notifications";
const NOTIFICATION_EXPIRY_HOURS = 48;

/**
 * Map API notification item to Notification type
 * Parses notification_message to extract title and description
 */
export const mapApiNotificationToNotification = (
  apiItem: NotificationApiItem
): Notification => {
  const message = apiItem.notification_message || "";
  
  // Try to extract title from message (format: "Title: Description" or "Title - Description")
  let title = "Notification";
  let description = message;
  
  const colonIndex = message.indexOf(":");
  const dashIndex = message.indexOf(" - ");
  
  if (colonIndex > 0 && colonIndex < 100) {
    // Use text before colon as title
    title = message.substring(0, colonIndex).trim();
    description = message.substring(colonIndex + 1).trim();
  } else if (dashIndex > 0 && dashIndex < 100) {
    // Use text before dash as title
    title = message.substring(0, dashIndex).trim();
    description = message.substring(dashIndex + 3).trim();
  } else if (message.length > 50) {
    // If message is long, use first 50 chars as title
    title = message.substring(0, 50).trim() + "...";
    description = message;
  } else {
    // Use full message as title if it's short
    title = message;
    description = "";
  }

  return {
    id: apiItem.id.toString(),
    type: "tote_status_change", // Default type, API doesn't provide this
    title,
    description: description || message,
    status: apiItem.is_read ? "read" : "unread",
    createdAt: apiItem.created_at,
    // navigationPath and metadata can be added later if API provides them
  };
};

/**
 * Format time difference for notifications
 * - If less than 24 hours: show "Xh ago"
 * - If more than 24 hours: show "dd/mm/yyyy HH:MM"
 */
export const formatNotificationTime = (createdAt: string): string => {
  const now = new Date();
  const notificationDate = new Date(createdAt);
  const diffMs = now.getTime() - notificationDate.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    const hours = Math.floor(diffHours);
    if (hours === 0) {
      const minutes = Math.floor(diffMs / (1000 * 60));
      return minutes <= 1 ? "Just now" : `${minutes}m ago`;
    }
    return `${hours}h ago`;
  }

  // Format as dd/mm/yyyy HH:MM
  const day = String(notificationDate.getDate()).padStart(2, "0");
  const month = String(notificationDate.getMonth() + 1).padStart(2, "0");
  const year = notificationDate.getFullYear();
  const hours = String(notificationDate.getHours()).padStart(2, "0");
  const minutes = String(notificationDate.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Remove notifications older than 48 hours
 */
export const removeExpiredNotifications = (
  notifications: Notification[]
): Notification[] => {
  const now = new Date();
  const expiryMs = NOTIFICATION_EXPIRY_HOURS * 60 * 60 * 1000;

  return notifications.filter((notification) => {
    const notificationDate = new Date(notification.createdAt);
    const diffMs = now.getTime() - notificationDate.getTime();
    return diffMs < expiryMs;
  });
};

/**
 * Load notifications from localStorage
 */
export const loadNotifications = (): Notification[] => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!stored) return [];
    const notifications: Notification[] = JSON.parse(stored);
    // Remove expired notifications
    const validNotifications = removeExpiredNotifications(notifications);
    // Save back if any were removed
    if (validNotifications.length !== notifications.length) {
      saveNotifications(validNotifications);
    }
    return validNotifications;
  } catch (error) {
    console.error("Error loading notifications:", error);
    return [];
  }
};

/**
 * Save notifications to localStorage
 */
export const saveNotifications = (notifications: Notification[]): void => {
  try {
    const validNotifications = removeExpiredNotifications(notifications);
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(validNotifications));
  } catch (error) {
    console.error("Error saving notifications:", error);
  }
};

/**
 * Get unread count
 */
export const getUnreadCount = (notifications: Notification[]): number => {
  return notifications.filter((n) => n.status === "unread").length;
};

/**
 * Sort notifications by date (most recent first)
 */
export const sortNotificationsByDate = (
  notifications: Notification[]
): Notification[] => {
  return [...notifications].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

